import Action from '../../../decorators/Action';
import Computed from '../../../decorators/Computed';
import Observable from '../../../decorators/Observable';
import AbstractEntity from './AbstractEntity';
import IKeywordsEntity from './IKeywordsEntity';

export default class ActivityEntity
  extends AbstractEntity
  implements IKeywordsEntity
{
  public readonly type = 'ActivityEntity';

  @Observable()
  public data: {
    checked: boolean;
    id: number | string;
    displayName: string;
    name: string;
    keywords: string[];
    subcategories: {
      checked: boolean;
      displayName: string;
      id: number | string;
      keywords: string[];
      name: string;

      areKeywordsProvided?: boolean;
      checkedKeywords?: string[];
      customAddedKeywords?: string[];
    }[];

    customSubcategories?: {
      checked: boolean;
      displayName: string;
      id: number | string;
      keywords: string[];
      name: string;

      areKeywordsProvided?: boolean;
      checkedKeywords?: string[];
      customAddedKeywords?: string[];
    }[];

    isCustom?: boolean;
    checkedKeywords?: string[];
    customAddedKeywords?: string[];

    areKeywordsProvided?: boolean;
    areAreasProvided?: boolean;
  } = null;

  constructor(data) {
    super();

    this.data = data;
    this.data.subcategories = this.data.subcategories
      ? this.data.subcategories.map((area) => ({
          ...area,
          areKeywordsProvided: area.areKeywordsProvided || false,
          checkedKeywords: area.checkedKeywords || [],
          customAddedKeywords: area.customAddedKeywords || []
        }))
      : [];

    this.data.checkedKeywords = this.data.checkedKeywords || [];
    this.data.customAddedKeywords = this.data.customAddedKeywords || [];

    this.data.areAreasProvided = this.data.areAreasProvided || false;
    this.data.areKeywordsProvided = this.data.areKeywordsProvided || false;

    this.data.customSubcategories = this.data.customSubcategories
      ? this.data.customSubcategories.map((area) => ({
          ...area,
          areKeywordsProvided: area.areKeywordsProvided || false,
          checkedKeywords: area.checkedKeywords || [],
          customAddedKeywords: area.customAddedKeywords || []
        }))
      : [];

    this.initFields();
  }

  setAreaKeywordsProvided(value: boolean): void {
    throw new Error('Method not implemented.');
  }

  @Action()
  public setChecked(value: boolean): void {
    this.data.checked = value;
  }

  @Action()
  public setAreasProvided(value: boolean): void {
    this.data.areAreasProvided = value;
  }

  @Action()
  setAreaChecked(id: number | string, value: boolean): void {
    const area = this.areas.find((area) => area.id === id);
    if (area) {
      area.checked = value;
    }
  }

  @Action()
  setKeywordsProvidedForArea(id: number | string, value: boolean): void {
    const area = this.areas.find((area) => area.id === id);
    if (area) {
      area.areKeywordsProvided = value;
    }
  }

  @Action()
  removeAllCustomKeywrodsForArea(areaId: number | string): void {
    const area = this.areas.find((area) => area.id === areaId);
    if (area) {
      area.customAddedKeywords = [];
    }
  }

  @Action()
  setCustomAreaKeywords(areaId: number | string, keywords: string[]): void {
    const area = this.areas.find((area) => area.id === areaId);
    if (area) {
      area.customAddedKeywords = keywords.filter(
        (keyword) => keyword.trim() !== ''
      );
    }
  }

  @Action()
  setAreaKeywordChecked(
    areaId: number | string,
    keyword: string,
    value: boolean
  ): void {
    const area = this.areas.find((area) => area.id === areaId);
    if (area) {
      if (value) {
        if (!area.checkedKeywords.includes(keyword)) {
          area.checkedKeywords.push(keyword);
        }
      } else {
        const index = area.checkedKeywords.indexOf(keyword);
        if (index > -1) {
          area.checkedKeywords.splice(index, 1);
        }
      }
    }
  }

  @Action()
  removeAllCustomAreas(): void {
    this.data.customSubcategories = [];
  }

  @Action()
  setCustomAreas(
    areas: {
      name: string;
    }[]
  ): void {
    this.data.customSubcategories = areas.map((area) => ({
      checked: true,
      id: area.name + '_custom', // Assuming id is the same as name for custom areas
      name: area.name,
      displayName: area.name,

      keywords: [],
      areKeywordsProvided: false,
      checkedKeywords: [],
      customAddedKeywords: []
    }));
  }

  isAreaKeywordChecked(areaId: number | string, keyword: string): boolean {
    const area = this.areas.find((area) => area.id === areaId);
    if (area) {
      return area.checkedKeywords.includes(keyword);
    }
    return false;
  }

  @Computed()
  get firstAreaWithNoKeywordsProvided(): {
    id: number | string;
    displayName: string;
    checked: boolean;
    keywords: string[];

    areKeywordsProvided?: boolean;
  } {
    const area = this.areas.find(
      (area) => !area.areKeywordsProvided && area.checked
    );

    return area || null;
  }

  @Computed()
  get areAreasProvided(): boolean {
    return this.data.areAreasProvided;
  }

  @Computed()
  get isCustomActivity(): boolean {
    return this.data.isCustom;
  }

  @Computed()
  get isChecked(): boolean {
    return this.data.checked;
  }

  @Computed()
  get hasAreas(): boolean {
    return this.areas && this.areas.length > 0;
  }

  @Computed()
  get nonCustomAreas(): {
    checked: boolean;
    displayName: string;
    id: number | string;
    keywords: string[];
    name: string;

    areKeywordsProvided?: boolean;
    checkedKeywords?: string[];
    customAddedKeywords?: string[];
  }[] {
    return this.data.subcategories;
  }

  @Computed()
  get areas(): {
    checked: boolean;
    displayName: string;
    id: number | string;
    keywords: string[];
    name: string;

    areKeywordsProvided?: boolean;
    checkedKeywords?: string[];
    customAddedKeywords?: string[];
  }[] {
    return [...this.data.subcategories, ...this.data.customSubcategories];
  }

  @Computed()
  get displayName(): string {
    return this.data.displayName;
  }

  @Computed()
  get name(): string {
    return this.data.name;
  }

  @Computed()
  get customAreas() {
    return this.data.customSubcategories;
  }

  @Computed()
  get id(): number | string {
    return this.data.id;
  }

  public getCheckedKeywordsForArea(areaId: number | string): string[] {
    const area = this.areas.find((area) => area.id === areaId);
    if (area) {
      return [...area.checkedKeywords, ...area.customAddedKeywords];
    }
    return [];
  }

  public getNonCustomCheckedKeywordsForArea(areaId: number | string): string[] {
    const area = this.areas.find((area) => area.id === areaId);
    if (area) {
      return area.checkedKeywords;
    }
    return [];
  }

  public getCustomAddedKeywordsForArea(areaId: number | string): string[] {
    const area = this.areas.find((area) => area.id === areaId);
    if (area) {
      return area.customAddedKeywords;
    }
    return [];
  }

  getKeywordsForArea(areaId: number | string): string[] {
    const area = this.areas.find((area) => area.id === areaId);
    if (area) {
      return area.keywords;
    }

    return [];
  }

  static fromJSON(json: any): ActivityEntity {
    if (json === null) {
      return null;
    }

    return new ActivityEntity(json.data);
  }

  public getEntityType(): string {
    return this.type;
  }

  public getSessionKey(): string {
    return 'ActivityEnity' + this.data.id;
  }

  @Action()
  removeCustomAreaByName(name: string) {
    this.data.customSubcategories = this.data.customSubcategories.filter(
      (area) => area.displayName.toLowerCase() !== name
    );
  }
}
